import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link, Navigate } from "react-router-dom";
import { Cpu, FileText, LifeBuoy, ArrowUpRight, ShieldCheck, FilePen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import SubscriptionChoice from "@/pages/portal/SubscriptionChoice";

const COPY = {
  en: {
    overline: "Private space",
    welcome: "Welcome",
    unitsLabel: "You hold",
    unitsSuffix: "Titan Units",
    unitsExplainer:
      "A Titan Unit represents one indivisible unit of AI compute capacity operated by Titan Infra. Your Units are formally associated with a fraction of the machinery being deployed at our Batam site.",
    docsTitle: "Your documents",
    docsBody: "Contracts, statements and invoices are made available here as they are issued.",
    docsCta: "Open documents",
    simTitle: "Compute simulator",
    simBody: "Simulate how the capacity you hold could translate into GPU-hours and compute value at our indicative operating range.",
    simCta: "Open simulator",
    supportTitle: "Contact & support",
    supportBody: "Your account manager and the Titan Infra operations desk are reachable here for any question about your Units, statements or contracts.",
    supportCta: "Open support",
    kycNote: "Account verified · manually provisioned",
  },
  fr: {
    overline: "Espace privé",
    welcome: "Bienvenue",
    unitsLabel: "Vous détenez",
    unitsSuffix: "Titan Units",
    unitsExplainer:
      "Une Titan Unit représente une unité indivisible de capacité de calcul IA exploitée par Titan Infra. Vos Units vous associent formellement à une fraction de la machinerie déployée sur notre site de Batam.",
    docsTitle: "Vos documents",
    docsBody: "Contrats, relevés et factures sont mis à disposition ici au fur et à mesure de leur émission.",
    docsCta: "Voir mes documents",
    simTitle: "Simulateur de calcul",
    simBody: "Simulez ce que la capacité que vous détenez pourrait représenter en heures GPU et en valeur de calcul, dans notre plage opérationnelle indicative.",
    simCta: "Ouvrir le simulateur",
    supportTitle: "Contact & support",
    supportBody: "Votre gestionnaire de compte et le desk opérationnel Titan Infra sont joignables ici pour toute question sur vos Units, vos relevés ou vos contrats.",
    supportCta: "Contacter le support",
    kycNote: "Compte vérifié · créé manuellement",
  },
  es: {
    overline: "Espacio privado",
    welcome: "Bienvenido",
    unitsLabel: "Dispones de",
    unitsSuffix: "Titan Units",
    unitsExplainer:
      "Una Titan Unit representa una unidad indivisible de capacidad de cálculo IA operada por Titan Infra. Tus Units te asocian formalmente a una fracción de la maquinaria desplegada en nuestro sitio de Batam.",
    docsTitle: "Tus documentos",
    docsBody: "Contratos, extractos y facturas se ponen a disposición aquí a medida que se emiten.",
    docsCta: "Ver mis documentos",
    simTitle: "Simulador de cálculo",
    simBody: "Simula lo que la capacidad que dispones podría representar en horas GPU y valor de cómputo, dentro de nuestro rango operativo indicativo.",
    simCta: "Abrir el simulador",
    supportTitle: "Contacto y soporte",
    supportBody: "Tu gestor de cuenta y el equipo de operaciones Titan Infra están disponibles aquí para cualquier pregunta sobre tus Units, extractos o contratos.",
    supportCta: "Contactar soporte",
    kycNote: "Cuenta verificada · aprovisionamiento manual",
  },
};

export default function Dashboard() {
  const { user, api, token } = useAuth();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  const [units, setUnits] = useState(null);
  const [choiceStatus, setChoiceStatus] = useState(null);
  const [docCount, setDocCount] = useState(0);
  const [subStatus, setSubStatus] = useState(null);
  const [kyc, setKyc] = useState({ id_document: false, residence_proof: false, signed_contract: false });

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${api}/me/units`, { headers: h }).then(r => setUnits(r.data.titan_units)).catch(() => setUnits(0));
    axios.get(`${api}/me/subscription-choice`, { headers: h }).then(r => setChoiceStatus(r.data.status)).catch(() => setChoiceStatus("pending"));
    axios.get(`${api}/me/documents`, { headers: h }).then(r => {
      setDocCount(r.data.documents.length);
      const cats = new Set(r.data.documents.map(d => d.category));
      setKyc({
        id_document:     cats.has("id_document"),
        residence_proof: cats.has("residence_proof"),
        signed_contract: cats.has("signed_contract"),
      });
    }).catch(() => {});
    axios.get(`${api}/me/submission`, { headers: h }).then(r => setSubStatus(r.data.status)).catch(() => {});
  }, [api, token]);

  const kycDone = Object.values(kyc).filter(Boolean).length;
  const kycTotal = 3;

  const firstName = user?.full_name?.split(" ")[0] || user?.email;

  // Admin landed on /portal by mistake (e.g. old bookmark) — send them to their own space.
  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  // If the client has not yet been confirmed by admin (titan_units = 0),
  // show the self-service subscription wizard (pending) or the locked receipt (submitted).
  if (units !== null && units === 0 && (choiceStatus === "pending" || choiceStatus === "submitted")) {
    return <SubscriptionChoice onSubmitted={() => setChoiceStatus("submitted")} />;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-muted-foreground">{c.overline}</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">{c.welcome}, {firstName}.</h1>
        </div>
        <div data-testid="dashboard-kyc-chip" className="text-[11px] tracking-widest uppercase px-3 py-1.5 border border-emerald-500/40 text-emerald-400">
          <ShieldCheck className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" /> {c.kycNote}
        </div>
      </div>

      {/* ── Titan Units hero card ── */}
      <div data-testid="units-card" className="mt-10 rounded-2xl border border-border bg-gradient-to-br from-emerald-500/[0.06] via-transparent to-sky-500/[0.06] p-8 md:p-12">
        <div className="flex items-center gap-3 text-[11px] tracking-widest uppercase text-emerald-400">
          <span className="w-6 h-px bg-emerald-400" /> {c.overline}
        </div>
        <p className="mt-6 text-muted-foreground text-[13px] tracking-widest uppercase">{c.unitsLabel}</p>
        <p className="mt-4 flex items-baseline gap-3">
          <span data-testid="units-value" className="font-display font-light tracking-tighter text-6xl md:text-8xl leading-none">
            {units === null ? "…" : Number(units).toLocaleString("fr-FR").replace(/,/g, " ")}
          </span>
          <span className="font-display text-xl md:text-2xl tracking-tight text-muted-foreground">{c.unitsSuffix}</span>
        </p>
        <p className="mt-8 max-w-2xl text-[14px] md:text-[15px] leading-relaxed text-muted-foreground">{c.unitsExplainer}</p>
      </div>

      {/* ── Subscription call-out ── */}
      {subStatus && subStatus !== "submitted" && (
        <Link
          to="/portal/subscription"
          data-testid="subscription-callout"
          className="mt-10 group block rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 md:p-7 hover:bg-amber-500/[0.10] transition-colors"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <FilePen className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="font-display text-[18px] tracking-tight">
                  {lang === "fr" ? "Complétez votre formulaire de souscription"
                  : lang === "es" ? "Completa tu formulario de suscripción"
                  : "Complete your subscription form"}
                </p>
                <p className="mt-1 text-[13px] text-muted-foreground max-w-2xl">
                  {lang === "fr" ? "Remplissez l'ICAF en ligne, signez électroniquement, et il sera automatiquement ajouté à vos documents."
                  : lang === "es" ? "Rellena el ICAF en línea, firma electrónicamente, y se añadirá automáticamente a tus documentos."
                  : "Fill in the ICAF online, sign electronically — it will be added to your documents automatically."}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-amber-400 group-hover:text-amber-300">
              {lang === "fr" ? "Ouvrir" : lang === "es" ? "Abrir" : "Open"} <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      )}

      {/* ── KYC checklist ── */}
      {kycDone < kycTotal && (
        <Link
          to="/portal/documents"
          data-testid="kyc-callout"
          className="mt-6 group block rounded-2xl border border-sky-500/30 bg-sky-500/[0.06] p-6 md:p-7 hover:bg-sky-500/[0.10] transition-colors"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-sky-400 mt-0.5" />
              <div>
                <p className="font-display text-[18px] tracking-tight">
                  {lang === "fr" ? `Complétez votre dossier KYC — ${kycDone}/${kycTotal} pièces déposées`
                  : lang === "es" ? `Completa tu KYC — ${kycDone}/${kycTotal} piezas subidas`
                  : `Complete your KYC — ${kycDone}/${kycTotal} pieces on file`}
                </p>
                <p className="mt-1 text-[13px] text-muted-foreground max-w-2xl">
                  {lang === "fr" ? "Déposez votre pièce d'identité, votre justificatif de domicile et votre contrat signé — depuis votre espace, en quelques secondes."
                  : lang === "es" ? "Sube tu documento de identidad, tu justificante de domicilio y tu contrato firmado desde tu espacio, en segundos."
                  : "Upload your identity document, proof of residence and signed contract from your space, in seconds."}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-sky-400 group-hover:text-sky-300">
              {lang === "fr" ? "Ouvrir" : lang === "es" ? "Abrir" : "Open"} <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      )}

      {/* ── Quick actions ── */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        <QuickCard to="/portal/documents" testId="quick-documents" icon={FileText} title={c.docsTitle} body={c.docsBody} cta={c.docsCta} badge={docCount > 0 ? `${docCount}` : null} />
        <QuickCard to="/portal/simulator" testId="quick-simulator" icon={Cpu} title={c.simTitle} body={c.simBody} cta={c.simCta} />
        <QuickCard to="/portal/support"   testId="quick-support"   icon={LifeBuoy} title={c.supportTitle} body={c.supportBody} cta={c.supportCta} />
      </div>
    </motion.div>
  );
}

const QuickCard = ({ to, icon: I, title, body, cta, testId, badge }) => (
  <Link to={to} data-testid={testId} className="group block rounded-2xl border border-border bg-card p-6 md:p-7 hover:bg-secondary/30 transition-colors">
    <div className="flex items-start justify-between">
      <I className="w-5 h-5 text-emerald-400" />
      {badge && <span className="text-[10px] tracking-widest uppercase border border-border px-2 py-1 rounded-full">{badge}</span>}
    </div>
    <p className="mt-6 font-display text-xl tracking-tight">{title}</p>
    <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">{body}</p>
    <p className="mt-6 inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-foreground group-hover:text-emerald-400 transition-colors">
      {cta} <ArrowUpRight className="w-3.5 h-3.5" />
    </p>
  </Link>
);
